import {format, formatDistanceStrict} from "date-fns"

import {phraseReturnReason} from "./returnReasonPhrasing"
import {phraseWaitParty} from "./waitPartyPhrasing"

import type {CaseReturnReason, CaseWaitParty} from "@indebted/api"

type AttentionStateName = "waiting-with-deadline" | "waiting-overdue" | "waiting-open-ended" | "needs-attention"

type CaseWait = {
	WaitParty?: string | null
	WaitUntil?: string | null
	ParkedAt?: string | null
	ReturnReason?: CaseReturnReason | null
	ReturnedAt?: string | null
}

type CaseAttentionState = {
	Name: AttentionStateName
	Label: string
	Detail: string | null
}

function phraseCaseAttentionState(caseWait: CaseWait, waitParties: CaseWaitParty[]): CaseAttentionState {
	const party = findWaitParty(caseWait, waitParties)
	if (!party) return {Name: "needs-attention", Label: "Needs attention", Detail: phraseReturn(caseWait)}

	return {
		Name: nameWaitState(caseWait, party),
		Label: phraseWaitParty(party).Status,
		Detail: phraseWaitLength(caseWait),
	}
}

function isWaitingOnParty(caseWait: CaseWait, waitParties: CaseWaitParty[]): boolean {
	return findWaitParty(caseWait, waitParties) !== undefined
}

function findWaitParty(caseWait: CaseWait, waitParties: CaseWaitParty[]): CaseWaitParty | undefined {
	return waitParties.find((party) => party.ID === caseWait.WaitParty)
}

function nameWaitState(caseWait: CaseWait, party: CaseWaitParty): AttentionStateName {
	if (!party.HasDeadline) return "waiting-open-ended"
	if (caseWait.WaitUntil && hasDeadlinePassed(caseWait.WaitUntil)) return "waiting-overdue"
	return "waiting-with-deadline"
}

function hasDeadlinePassed(waitUntil: string): boolean {
	return new Date(waitUntil).getTime() < Date.now()
}

// A wait the party owes no dated response to has no deadline to count down to,
// so it reads as how long it has been waiting instead. Every park has always
// recorded the moment it happened, so a case that reaches here waiting carries
// ParkedAt; the field is optional only for a case that was never parked.
function phraseWaitLength(caseWait: CaseWait): string | null {
	if (caseWait.WaitUntil) {
		const deadlineDay = format(new Date(caseWait.WaitUntil), "d MMM yyyy")
		return hasDeadlinePassed(caseWait.WaitUntil) ? `overdue since ${deadlineDay}` : `until ${deadlineDay}`
	}
	if (caseWait.ParkedAt) return `for ${formatDistanceStrict(new Date(caseWait.ParkedAt), new Date())}`
	return null
}

function phraseReturn(caseWait: CaseWait): string | null {
	if (!caseWait.ReturnReason || !caseWait.ReturnedAt) return null
	return `Returned ${format(new Date(caseWait.ReturnedAt), "d MMM yyyy")} — ${phraseReturnReason(caseWait.ReturnReason)}`
}

export {isWaitingOnParty, phraseCaseAttentionState}
export type {AttentionStateName, CaseWait}
